import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MembershipProps } from '@dosfilos/domain';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  type DateFormat,
  type TimeFormat,
} from '@dosfilos/shared';
import type { DomainType } from '../lib/resolve-tenant-by-domain';

export interface TenantTheme {
  primaryColor?: string;
  name?: string;
  slug?: string;
  logoUrl?: string;
}

/** Active platform-admin "view-as-tenant" override. Set when a platformAdmin
 *  enters a tenant from the super-admin console to debug it. Persisted so the
 *  banner + the active tenant survive a reload; cleared on exit. `homeTenantId`
 *  is the platformAdmin's own tenant to return to. */
export interface PlatformViewAs {
  tenantId: string;
  tenantName: string;
  homeTenantId: string | null;
}

/** The seminary derived from the CURRENT custom domain (by hostname). Authority
 *  for tenant scoping + the landing/backoffice route role. NOT persisted — it is
 *  re-derived from the hostname every session (the hostname can't be spoofed in
 *  the way localStorage can be stale). */
export interface DomainContext {
  tenantId: string;
  slug: string;
  type: DomainType;
}

interface TenantSlice {
  currentTenantId: string | null;
  currentMembership: MembershipProps | null;
  availableTenants: { id: string; name: string; slug: string }[];
  currentTenantTheme: TenantTheme | null;
  /** The active tenant's IANA zone (localization). Extracted from the tenant doc
   *  (admin/staff path) or the domain resolver (portal/hostname path). Null until
   *  resolved → consumers fall back via `useTenantTimeZone()`. */
  currentTenantTimeZone: string | null;
  /** The active tenant's numeric date ordering. Same sources as the zone. Null until
   *  resolved → consumers fall back via `useTenantDateFormat()`. */
  currentTenantDateFormat: DateFormat | null;
  /** The active tenant's clock convention. Same sources as the zone. */
  currentTenantTimeFormat: TimeFormat | null;
  platformViewAs: PlatformViewAs | null;
  /** Set when on a tenant's custom domain; null on the platform/base host. */
  currentDomain: DomainContext | null;
  setCurrentDomain: (domain: DomainContext | null) => void;
  setCurrentTenantTimeZone: (tz: string | null) => void;
  setCurrentTenantDateFormat: (fmt: DateFormat | null) => void;
  setCurrentTenantTimeFormat: (fmt: TimeFormat | null) => void;
  setCurrentTenant: (tenantId: string, membership: MembershipProps) => void;
  /** Seed only the active tenant id (membership reloaded by auth.context).
   *  Used right after invite activation to land in the invited tenant. */
  setCurrentTenantId: (tenantId: string) => void;
  setAvailableTenants: (tenants: { id: string; name: string; slug: string }[]) => void;
  setTenantTheme: (theme: TenantTheme) => void;
  setPlatformViewAs: (viewAs: PlatformViewAs | null) => void;
  clearTenant: () => void;
}

export const useTenantStore = create<TenantSlice>()(
  persist(
    (set) => ({
      currentTenantId: null,
      currentMembership: null,
      availableTenants: [],
      currentTenantTheme: null,
      currentTenantTimeZone: null,
      currentTenantDateFormat: null,
      currentTenantTimeFormat: null,
      platformViewAs: null,
      currentDomain: null,
      setCurrentDomain: (domain) => set({ currentDomain: domain }),
      setCurrentTenantTimeZone: (tz) => set({ currentTenantTimeZone: tz }),
      setCurrentTenantDateFormat: (fmt) => set({ currentTenantDateFormat: fmt }),
      setCurrentTenantTimeFormat: (fmt) => set({ currentTenantTimeFormat: fmt }),
      setCurrentTenant: (tenantId, membership) =>
        set({ currentTenantId: tenantId, currentMembership: membership }),
      setCurrentTenantId: (tenantId) => set({ currentTenantId: tenantId }),
      setAvailableTenants: (tenants) => set({ availableTenants: tenants }),
      setTenantTheme: (theme) => set({ currentTenantTheme: theme }),
      setPlatformViewAs: (viewAs) => set({ platformViewAs: viewAs }),
      clearTenant: () =>
        // NOTE: currentDomain is intentionally NOT cleared — it's derived from the
        // hostname (set by <DomainResolver/>), not from auth, so it must survive
        // logout. Clearing it would blind DomainRoleEnforcer for anonymous users.
        set({
          currentTenantId: null,
          currentMembership: null,
          availableTenants: [],
          currentTenantTheme: null,
          currentTenantTimeZone: null,
          currentTenantDateFormat: null,
          currentTenantTimeFormat: null,
          platformViewAs: null,
        }),
    }),
    {
      name: 'dosfilos-tenant',
      partialize: (state) => ({
        currentTenantId: state.currentTenantId,
        // Persist membership so role-based UI (isAdmin checks) survives page reloads
        currentMembership: state.currentMembership,
        // Persist theme so colors survive page reloads without waiting for Firestore
        currentTenantTheme: state.currentTenantTheme,
        // Persist the zone so dates render correctly immediately on reload
        currentTenantTimeZone: state.currentTenantTimeZone,
        // Persist the date ordering for the same reason
        currentTenantDateFormat: state.currentTenantDateFormat,
        // Persist the clock convention for the same reason
        currentTenantTimeFormat: state.currentTenantTimeFormat,
        // Persist platform view-as so the banner + active tenant survive reloads
        platformViewAs: state.platformViewAs,
      }),
    },
  ),
);

/** Default zone when the active tenant's zone hasn't resolved (or a legacy tenant
 *  has none). Chile — the founding institute. Mirrors the server-side
 *  `resolveTenantTimeZone` fallback so client and server agree. */
export const DEFAULT_TENANT_TIME_ZONE = 'America/Santiago';

/** The active tenant's IANA zone for date display, with the safe fallback.
 *  Use this everywhere a stored instant is rendered — never the browser zone. */
export function useTenantTimeZone(): string {
  return useTenantStore((s) => s.currentTenantTimeZone) ?? DEFAULT_TENANT_TIME_ZONE;
}

/** The active tenant's numeric date ordering, with the safe dd-mm-yyyy fallback.
 *  Pass to `formatInstant`/`formatDateOnly` alongside the zone. */
export function useTenantDateFormat(): DateFormat {
  return useTenantStore((s) => s.currentTenantDateFormat) ?? DEFAULT_DATE_FORMAT;
}

/** The active tenant's clock convention, with the safe 24h fallback. Pass to
 *  `formatInstant` after the zone + date format. */
export function useTenantTimeFormat(): TimeFormat {
  return useTenantStore((s) => s.currentTenantTimeFormat) ?? DEFAULT_TIME_FORMAT;
}
